import { Check, Copy, MessageCircle } from 'lucide-react';
import { useState } from 'react';

/**
 * A link an admin has to get to somebody, when email cannot be trusted to.
 *
 * This exists because of a real failure, not a hypothetical one. Production
 * email ran through a transport that held every activation link and instead
 * mailed the recipient a "confirm your address before we deliver anything"
 * notice. Approved people waited for a message that was never going to arrive,
 * and nobody on either side could see why.
 *
 * The lesson is not "fix the transport" — that was one bug and it is fixed. It
 * is that **email is a channel that sometimes does not work, and a flow with
 * email as its only path has a single point of failure in the middle of it.**
 * An admin in the office should be able to read this down a phone or paste it
 * into WhatsApp.
 *
 * Showing it grants nothing. The person reading it has just created this
 * account and chosen what it can do; a token scoped to confirming that same
 * account is strictly less authority than they already hold. Same reasoning as
 * the handover password on the Users screen.
 */
export function HandoverLink({
  url,
  who,
  what,
}: {
  url: string;
  /** The person it is for, so a copied link is not sent to the wrong one. */
  who: string;
  /** One line on what the link does, in their words rather than ours. */
  what: string;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /*
       * Clipboard access is refused in plenty of ordinary situations — an
       * insecure origin, a browser policy, a permission never granted. The link
       * is on screen and selectable regardless, which is why this fails quietly
       * rather than showing an error about a convenience.
       */
    }
  };

  /*
   * WhatsApp is first because it is what this office actually uses. `wa.me`
   * with no number opens the contact picker, which is the right behaviour: this
   * component does not know the person's number and should not guess.
   */
  const whatsapp = `https://wa.me/?text=${encodeURIComponent(
    `${what}\n\n${url}\n\nThis link works once and expires in two days.`,
  )}`;

  return (
    <div className="rounded-xl border border-navy-200 bg-navy-50 p-3">
      <p className="text-sm font-medium text-navy-700">Send this to {who}</p>
      <p className="mt-0.5 text-xs text-navy-700">
        {what} Do not wait for the email — send it yourself and you know it arrived.
      </p>

      {/*
        Selectable, wrapping, and in a monospace face. A link somebody may have
        to read aloud needs characters that cannot be confused, and one that
        might be copied by hand must not be truncated with an ellipsis.
      */}
      <code className="mt-2 block overflow-x-auto break-all rounded-lg bg-card px-3 py-2 text-xs">
        {url}
      </code>

      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={copy} className="btn-secondary px-3 py-1.5 text-xs">
          {copied ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {copied ? 'Copied' : 'Copy the link'}
        </button>

        <a
          href={whatsapp}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary px-3 py-1.5 text-xs"
        >
          <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
          Send on WhatsApp
        </a>
      </div>

      <p className="mt-2 text-xs text-navy-700">
        It works once and expires in two days. If it runs out, use “send the link again”.
      </p>
    </div>
  );
}
